// eslint-disable-next-line no-use-before-define
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import logError from 'App/babel/helpers/logError';
import {
	DrawerHeader,
	ButtonGroup,
	CardContent,
	Checkbox,
	Column,
	Button,
	Title,
	Block,
	Text,
	Card,
	Icon,
	Row,
	Loader
} from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import DrawerWrap from 'Components/DrawerWrap';
import T from 'Components/Helpers/translate';
import { getTierString } from 'App/babel/helpers/addons';
import {
	addSeats,
	removeSeats,
	addSupportSeats,
	removeSupportSeats,
	getActiveLicense,
	getSeatChangeInfo,
	getSupportSeatChangeInfo,
	getSubscriptionOrActiveChild,
	getOrderRows
} from 'Store/reducers/BillingReducer';
import { billingTracker } from 'App/babel/helpers/Tracker';
import Tooltip from '@upsales/components/Tooltip';
import moment from 'moment';
import { DangerButton } from '@upsales/components/Buttons';

import './BillingAddSeatDrawer.scss';

const getAddons = (addons, futureLicense) =>
	addons.reduce((res, addon) => {
		const futureAddon = getOrderRows(futureLicense).find(row => row.productId === addon.productId) ? true : false;
		addon = { ...addon, futureAddon };
		if (addon.productActive) {
			res.push(addon);
		}
		return res;
	}, []);

const mapStateToProps = ({ Billing }) => ({
	licencePrice: Billing.licenceInfo?.price ?? 0,
	intervalPeriod: Billing.licenceInfo?.agreementIntervalPeriod ?? 12,
	currency: Billing.licenceInfo?.currency ?? 'SEK',
	loading: Billing.loading,
	currentLicenses: Billing.maxLicences,
	currentSupportLicenses: Billing.maxSupportLicences,
	addons: getAddons(Billing.addons, Billing.futureLicense),
	futureLicense: Billing.futureLicense,
	seatChangeInfo: Billing.seatChangeInfo,
	supportSeatChangeInfo: Billing.supportSeatChangeInfo,
	loadingSeatInfo: Billing.loadingSeatInfo,
	loadingSupportSeatInfo: Billing.loadingSupportSeatInfo,
	activeLicense: Billing.activeLicense,
	invoiceSubscriptions: Billing.invoiceSubscriptions,
	licenceInfo: Billing.licenceInfo,
	supportLicenceInfo: Billing.supportLicenceInfo,
	userCount: Billing.userCount,
	supportUserCount: Billing.supportUserCount,
	billingAddress: Billing.billingAddress
});

const mapDispatchToProps = {
	addSeats,
	removeSeats,
	addSupportSeats,
	removeSupportSeats,
	getSeatChangeInfo,
	getSupportSeatChangeInfo
};

const currencyFormat = (num, cur) => Tools.$filter('currencyFormat')(num, cur);

const Addon = ({ addon, classes, subscriptionEnddate, futureLicense, currency, intervalPeriod, currencyRate = 1 }) => {
	let tierStr = '';
	if (addon.tier) {
		tierStr = getTierString({ ...addon, tier: addon.tier });
	}

	return (
		<div key={addon.productId}>
			<Block
				border="ts bs"
				borderColor="grey-4"
				space="pll prl ptm pbm"
				backgroundColor="white"
				className={classes.elem('add-on-row').b()}
			>
				<Row>
					<Column>
						<Text>{T(addon.name)}</Text>
						{futureLicense && !addon.futureAddon ? (
							<Text color="grey-10" size="sm">
								{`${T('admin.billing.cancelled')}: ${subscriptionEnddate}`}
							</Text>
						) : addon.tier && addon.basedOn === 'activeUsers' ? (
							<Text color="grey-10" size="sm">{`${addon.tier.start} - ${addon.tier.end || '∞'}`}</Text>
						) : null}
					</Column>
					<Column align="right">
						{addon.basedOn === 'activeUsers' && addon.type === 'quantity_based' ? (
							<>
								<Text>{`${addon.quantity} x ${currencyFormat(
									addon.price * currencyRate,
									currency
								)} / ${T('admin.billing.interval.' + intervalPeriod)}`}</Text>
								<Text color="grey-10" size="sm">
									{currencyFormat(addon.quantity * addon.price * currencyRate, currency)}
								</Text>
							</>
						) : (
							<Text>{`${currencyFormat(
								(addon.isTotalPrice ? 1 : addon.quantity) * addon.price * currencyRate,
								currency
							)} / ${T('admin.billing.interval.' + intervalPeriod)}`}</Text>
						)}
						{addon.tier ? (
							<Text color="grey-10" size="sm">
								{tierStr}
							</Text>
						) : null}
					</Column>
				</Row>
			</Block>
		</div>
	);
};

const BillingAddSeatDrawer = ({
	visible,
	onClose,
	currentLicenses,
	currentSupportLicenses,
	currency,
	addons,
	futureLicense,
	addSeats,
	removeSeats,
	addSupportSeats,
	removeSupportSeats,
	getSeatChangeInfo,
	getSupportSeatChangeInfo,
	loadingSeatInfo,
	loadingSupportSeatInfo,
	seatChangeInfo,
	supportSeatChangeInfo,
	intervalPeriod,
	licenceInfo,
	supportLicenceInfo,
	userCount,
	supportUserCount,
	nextPage,
	activeUsers,
	activeLicense,
	billingAddress,
	agreementGroup,
	loading,
	seatType = 'seats' // 'seats' or 'supportSeats'
}) => {
	const classes = new BemClass('BillingAddSeatDrawer');

	// Determine which values to use based on seatType
	const isSupportSeats = seatType === 'supportSeats';
	const effectiveCurrentLicenses = isSupportSeats ? currentSupportLicenses : currentLicenses;
	const effectiveUserCount = isSupportSeats ? supportUserCount : userCount;
	const effectiveLicenceInfo = isSupportSeats ? supportLicenceInfo : licenceInfo;
	const effectiveLoadingSeatInfo = isSupportSeats ? loadingSupportSeatInfo : loadingSeatInfo;
	const effectiveSeatChangeInfo = isSupportSeats ? supportSeatChangeInfo : seatChangeInfo;

	const futureLicenses = isSupportSeats
		? 0 // Support seats don't have separate future seat rows currently
		: licenceInfo?.futureSeatRows?.reduce((totalQuantity, row) => (totalQuantity += row.quantity), 0) ?? 0; //if 0, there is no future license, as it can only go down to 1.

	const [seats, setSeats] = useState(activeUsers ?? effectiveCurrentLicenses);
	const [authorized, setAuthorized] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	let diffOrderPrice = 0;
	let priceDiffPerInterval = 0;
	const activeLicenseOrChild = getSubscriptionOrActiveChild(activeLicense);
	const activeCurrencyRate = activeLicenseOrChild?.currencyRate ?? activeLicense?.currencyRate ?? 1;

	useEffect(() => {
		getActiveLicense();
	}, []);

	useEffect(() => {
		if (activeUsers) {
			setSeats(activeUsers);
		}
	}, [activeUsers]);

	useEffect(() => {
		setSeats(activeUsers ?? effectiveCurrentLicenses);
	}, [seatType, effectiveCurrentLicenses]);

	useEffect(() => {
		if (seats !== effectiveCurrentLicenses) {
			if (isSupportSeats) {
				getSupportSeatChangeInfo(seats - effectiveCurrentLicenses);
			} else {
				getSeatChangeInfo(seats - effectiveCurrentLicenses);
			}
		}

		diffOrderPrice = 0;
		priceDiffPerInterval = 0;
	}, [seats]);

	const [showPricingChange, setShowPricingChange] = useState(false);

	// Handle support seat change info (simpler structure)
	if (isSupportSeats && effectiveSeatChangeInfo && seats !== effectiveCurrentLicenses) {
		const { diffOrder } = effectiveSeatChangeInfo;
		if (diffOrder) {
			diffOrderPrice = diffOrder.totalValue;
		}
		priceDiffPerInterval = effectiveSeatChangeInfo.priceDiff || 0;
	}
	// Handle regular seat change info
	else if (!isSupportSeats && seatChangeInfo && seats !== effectiveCurrentLicenses) {
		const { diffOrder, newSubscription } = seatChangeInfo;

		if (diffOrder) {
			diffOrderPrice = diffOrder.totalValue;
		}

		if (newSubscription && activeLicense) {
			const activeOrderRows = getOrderRows(activeLicense);
			const newOrderRows = getOrderRows(newSubscription);

			const previousCost = activeOrderRows.reduce((total, row) => (total += row.quantity * row.price), 0);
			const newCost = newOrderRows.reduce((total, row) => (total += row.quantity * row.price), 0);

			const newOrderInterval = newSubscription?.metadata?.agreementIntervalPeriod;
			const previousOrderInterval = activeLicense?.metadata?.agreementIntervalPeriod;

			if (!newOrderInterval || !previousOrderInterval) {
				priceDiffPerInterval = 0;
			} else {
				const orderIntervalRatio = newOrderInterval / previousOrderInterval;

				priceDiffPerInterval = newCost / orderIntervalRatio - previousCost;
				priceDiffPerInterval *= activeCurrencyRate;
				addons?.forEach(addon => {
					const currentAddonRow = activeOrderRows.find(row => row.productId === addon.productId);
					const updatedAddonRow = newOrderRows.find(row => row.productId === addon.productId);

					if (currentAddonRow && updatedAddonRow) {
						const currentPrice = currentAddonRow.quantity * currentAddonRow.price;
						const updatedPrice = (updatedAddonRow.quantity * updatedAddonRow.price) / orderIntervalRatio;

						addon.affected = currentPrice !== updatedPrice;
						addon.price = updatedAddonRow.price / orderIntervalRatio;
						addon.quantity = updatedAddonRow.quantity;
					}
				});
			}
		}
	}

	const changeSeats = async () => {
		if (submitting) {
			return;
		}
		setSubmitting(true);
		try {
			if (isSupportSeats) {
				// Support seats flow
				if (seats > effectiveCurrentLicenses) {
					billingTracker.track(billingTracker.events.ADD_SUPPORT_SEATS, { seats });
					await addSupportSeats(seats - effectiveCurrentLicenses);
					const metadata = Tools.AppService.getMetadata();
					Tools.AppService.setMetadata({ ...metadata, supportLicenses: seats });
				} else if (seats < effectiveCurrentLicenses) {
					billingTracker.track(billingTracker.events.REMOVE_SUPPORT_SEATS, { seats });
					await removeSupportSeats(seats);
				}
			} else {
				// Regular seats flow
				if (seats > effectiveCurrentLicenses) {
					billingTracker.track(billingTracker.events.ADD_SEATS, { seats });
					const addedSeats = await addSeats(seats - effectiveCurrentLicenses, agreementGroup);
					const metadata = Tools.AppService.getMetadata();
					Tools.AppService.setMetadata({ ...metadata, licenses: seats });
					if (nextPage) {
						Promise.all([addedSeats])
							.then(() => {
								nextPage('added_seats');
							})
							.catch(function (e) {
								setSubmitting(false);
								logError(e, 'Error occured while adding seats');
							});
					}
				} else if (seats < effectiveCurrentLicenses) {
					billingTracker.track(billingTracker.events.REMOVE_SEATS, { seats });
					await removeSeats(seats, agreementGroup);
				}
			}
			onClose(true);
		} catch (e) {
			logError(e, 'Error occurred while changing seats');
		} finally {
			setSubmitting(false);
		}
	};
	const handleCancel = () => {
		onClose(false);
		setSeats(activeUsers ?? effectiveCurrentLicenses);
	};

	const getDecreaseDate = () => {
		const activeRenewalDate = activeLicense?.metadata?.agreementRenewalDate;
		const renewalDate = activeRenewalDate ? moment(activeRenewalDate) : moment().add(1, 'year');
		const todaysDate = moment();
		const daysUntilRenewal = renewalDate.diff(todaysDate, 'days');
		let subscriptionEnddate;
		if (daysUntilRenewal >= 90) {
			subscriptionEnddate = renewalDate.format('YYYY-MM-DD');
		} else {
			subscriptionEnddate = moment(renewalDate)
				.add(activeLicense?.metadata?.agreementIntervalPeriod ?? 0, 'M')
				.format('YYYY-MM-DD');
		}
		return subscriptionEnddate;
	};

	const alreadyDowngraded = () => {
		const licenseDiff = effectiveCurrentLicenses - futureLicenses;
		const newSeats = seats - effectiveCurrentLicenses + futureLicenses;

		const licenseDiffLanguage = T(licenseDiff === 1 ? 'admin.billing.seat' : 'admin.billing.seats').toLowerCase();
		const seatsLanguage = T(seats === 1 ? 'admin.billing.seat' : 'admin.billing.seats').toLowerCase();
		const newSeatsLanguage = T(newSeats === 1 ? 'admin.billing.seat' : 'admin.billing.seats').toLowerCase();

		const alreadyDowngradedPart1 = T('admin.billing.alreadyDowngraded.part1', {
			licenseDiff,
			licenseDiffLanguage
		});

		const alreadyDowngradedPart2 = T('admin.billing.alreadyDowngraded.part2', {
			seats,
			seatsLanguage,
			decreaseDate: getDecreaseDate()
		});

		const alreadyDowngradedPart3 = T('admin.billing.alreadyDowngraded.part3', {
			newSeats,
			newSeatsLanguage
		});

		return `${alreadyDowngradedPart1} ${alreadyDowngradedPart2} ${alreadyDowngradedPart3}`;
	};

	const upgradeDowngrade = () => {
		return T('admin.billing.upgradeDowngrade', {
			futureLicenses: futureLicenses,
			seats: seats,
			decreaseDate: getDecreaseDate(),
			currentLicenses: effectiveCurrentLicenses
		});
	};

	const renewalDate = activeLicense?.metadata?.agreementRenewalDate;
	const noticeDateIsInFuture = renewalDate && moment(renewalDate).subtract(3, 'months').isAfter(moment());
	const willBeRenewed = futureLicense || !activeLicense?.metadata?.agreementEnddate;

	const canDecrease = seats > effectiveCurrentLicenses || (willBeRenewed && noticeDateIsInFuture);

	// Show loading state while billing data is being fetched
	if (loading) {
		return (
			<DrawerWrap className={classes.b()} isMounted={visible} onClose={handleCancel}>
				<DrawerHeader
					icon="users"
					iconColor="blue"
					onHide={handleCancel}
					title={T('admin.billing.editSeats')}
				/>
				<Block space="pll prl ptl pbl" backgroundColor="white">
					<Row align="center">
						<Loader size="md" />
					</Row>
				</Block>
			</DrawerWrap>
		);
	}

	const accountSelf = Tools.AppService.getAccountSelf();
	if (!accountSelf) {
		return null;
	}
	const hasCorrectVerion = ['starter', 'accelerate'].includes(accountSelf.version.toLowerCase());
	const hasLessThanTwoSeats = seats < 2;

	let decreaseDisabledTooltip = T('admin.billing.decreaseDisabled');
	if (willBeRenewed && !noticeDateIsInFuture) {
		decreaseDisabledTooltip = T('admin.billing.decreaseDisabledNoticeDate', {
			date: moment(renewalDate).format('L')
		});
	}

	const increaseDisabled = !effectiveLicenceInfo && seats >= effectiveCurrentLicenses;
	const increaseDisabledTooltip = T(
		isSupportSeats ? 'admin.billing.supportIncreaseDisabledTooltip' : 'admin.billing.increaseDisabledTooltip'
	);

	// Translation keys based on seat type
	const editSeatsTitle = T(isSupportSeats ? 'admin.billing.editSupportSeats' : 'admin.billing.editSeats');
	const totalSeatsLabel = T(isSupportSeats ? 'admin.billing.totalSupportSeats' : 'admin.billing.totalSeats');
	const availableSeatsKey = isSupportSeats ? 'admin.billing.availableSupportSeats' : 'admin.billing.availableSeats';
	const availableSeatKey = isSupportSeats ? 'admin.billing.availableSupportSeat' : 'admin.billing.availableSeat';
	const licenseKey = isSupportSeats ? 'admin.billing.supportLicense' : 'admin.billing.license';
	const licensesKey = isSupportSeats ? 'admin.billing.supportLicenses' : 'admin.billing.licenses';

	return (
		<DrawerWrap className={classes.b()} isMounted={visible} onClose={handleCancel}>
			<DrawerHeader icon="users" iconColor="blue" onHide={handleCancel} title={editSeatsTitle} />
			<Block
				className={classes.elem('content').b()}
				border="bs"
				borderColor="grey-4"
				space="pll prl ptl pbl"
				backgroundColor="white"
			>
				<Row>
					<Column>
						<Text className={classes.elem('content').elem('totalSeats').b()}>{totalSeatsLabel}</Text>
					</Column>
					<Column align="right" className={classes.elem('licence-input-col').b()}>
						<ButtonGroup>
							<Tooltip title={decreaseDisabledTooltip} disabled={canDecrease}>
								<Button
									className="ButtonGroup__no-flex"
									color="light-grey"
									size="sm"
									shadow="none"
									onClick={() => setSeats(seats - 1)}
									loading={effectiveLoadingSeatInfo}
									disabled={
										(!hasCorrectVerion && seats <= effectiveCurrentLicenses) ||
										hasLessThanTwoSeats ||
										!canDecrease
									}
								>
									{'-'}
								</Button>
							</Tooltip>
							<Block color="white" border="ts bs" borderColor="grey-4">
								<Text size="sm">{seats}</Text>
							</Block>
							<Tooltip title={increaseDisabledTooltip} disabled={!increaseDisabled}>
								<Button
									className="ButtonGroup__no-flex"
									color="light-grey"
									size="sm"
									shadow="none"
									loading={effectiveLoadingSeatInfo}
									disabled={increaseDisabled}
									onClick={() => setSeats(seats + 1)}
								>
									{'+'}
								</Button>
							</Tooltip>
						</ButtonGroup>
					</Column>
				</Row>
			</Block>

			{seats !== effectiveCurrentLicenses && futureLicense && futureLicenses !== effectiveCurrentLicenses ? (
				<Block space="pll prl ptm pbm">
					{seats > effectiveCurrentLicenses ? (
						<Text size="sm">{alreadyDowngraded()}</Text>
					) : (
						<Text size="sm">{upgradeDowngrade()}</Text>
					)}
				</Block>
			) : seats < effectiveCurrentLicenses ? (
				<Block space="pll prl ptm pbm">
					<Text size="sm">
						{T('admin.billing.decreaseLicences', {
							decreaseCount: effectiveCurrentLicenses - seats,
							decreaseDate: getDecreaseDate()
						})}
					</Text>
				</Block>
			) : null}
			<Row>
				<Column className={classes.elem('expandable-text-left').b()}>
					{effectiveCurrentLicenses === 1 ? (
						<Text color="grey-10">{T(availableSeatKey, { count: effectiveCurrentLicenses })}</Text>
					) : (
						<Text color="grey-10">{T(availableSeatsKey, { count: effectiveCurrentLicenses })}</Text>
					)}
				</Column>
				<Column align="right" className={classes.elem('expandable-text-right').b()}>
					{!isSupportSeats && addons.length > 0 ? (
						showPricingChange ? (
							<Button type="link" onClick={() => setShowPricingChange(!showPricingChange)}>
								<Icon name="eye-slash" /> {T('admin.billing.pricingChangeHide')}
							</Button>
						) : (
							<Button type="link" onClick={() => setShowPricingChange(!showPricingChange)}>
								<Icon name="eye" /> {T('admin.billing.pricingChangeShow')}
							</Button>
						)
					) : null}
				</Column>
			</Row>

			{showPricingChange ? (
				<div className={classes.elem('addon_list').b()}>
					{addons?.some(addon => addon.affected) ? (
						<Block space="pll prl ptxl pbm">
							<Title size="sm" bold={true}>
								{T('admin.billing.pricingWillChangeOn')}
							</Title>
						</Block>
					) : null}
					{addons?.map(addon =>
						addon.affected ? (
							<Addon
								key={addon.alias}
								addon={addon}
								intervalPeriod={intervalPeriod}
								futureLicense={futureLicense}
								currency={currency}
								currencyRate={activeCurrencyRate}
								classes={classes}
								subscriptionEndDate={getDecreaseDate()}
							/>
						) : null
					)}

					{addons?.some(addon => !addon.affected) ? (
						<Block space="pll prl ptxl pbm">
							<Title size="sm" bold={true}>
								{T('admin.billing.pricingNotChanged')}
							</Title>
						</Block>
					) : null}

					{addons?.map(addon =>
						!addon.affected ? (
							<Addon
								key={addon.alias}
								addon={addon}
								intervalPeriod={intervalPeriod}
								futureLicense={futureLicense}
								currency={currency}
								currencyRate={activeCurrencyRate}
								classes={classes}
								subscriptionEndDate={getDecreaseDate()}
							/>
						) : null
					)}
				</div>
			) : null}

			<Card className={classes.elem('confirm-card').b()}>
				<CardContent>
					<Row>
						<Column>
							<Text color="grey-10">{T('admin.billing.cost.total.interval.' + intervalPeriod)}</Text>
						</Column>
						<Column align="right">
							{activeLicense ? (
								<>
									<Text loading={effectiveLoadingSeatInfo}>
										{effectiveLoadingSeatInfo
											? null
											: currencyFormat(
													priceDiffPerInterval + activeLicense.value,
													activeLicense.currency
											  )}
									</Text>
								</>
							) : null}
						</Column>
					</Row>
					{activeLicense && seats !== effectiveCurrentLicenses ? (
						<>
							<Row>
								<Column>
									<Text color="grey-10" size="sm">
										{T('admin.billing.cost.change.interval.' + intervalPeriod)}
									</Text>
								</Column>
								<Column align="right">
									<Text loading={effectiveLoadingSeatInfo} size="sm" color="grey-10">
										{effectiveLoadingSeatInfo
											? null
											: currencyFormat(Math.ceil(priceDiffPerInterval), activeLicense.currency)}
									</Text>
								</Column>
							</Row>
							<Row className={classes.elem('pay-now')}>
								<Column>
									<Text color="grey-10" size="sm">
										{T('admin.billing.payNow')}
									</Text>
								</Column>
								<Column align="right">
									<Text loading={effectiveLoadingSeatInfo} size="sm" color="grey-10">
										{effectiveLoadingSeatInfo
											? null
											: currencyFormat(
													Math.max(Math.ceil(diffOrderPrice), 0),
													activeLicense.currency
											  )}
									</Text>
								</Column>
							</Row>
						</>
					) : null}
					<Row>
						<Column align="right">
							<Block space="ptl">
								{seats !== effectiveCurrentLicenses ? (
									<Block className={classes.elem('confirm-card').elem('authorized').b()}>
										<Checkbox
											aria-label={T('admin.billing.iAmAuthorized')}
											checked={authorized}
											onChange={() => setAuthorized(v => !v)}
										></Checkbox>
										<Text>
											{T('admin.billing.iAmAuthorizedLong')}
											<span className={classes.elem('confirm-card').elem('requiredAsterisk')}>
												{' *'}
											</span>
										</Text>
									</Block>
								) : null}
								<div className={classes.elem('confirm-card').elem('cta').b()}>
									<Tooltip
										title={T('admin.billing.iAmAuthorized.tooltip')}
										disabled={seats === effectiveCurrentLicenses || authorized}
										position="bottom"
										distance={22}
									>
										{seats > effectiveCurrentLicenses ? (
											<Button
												size="lg"
												onClick={changeSeats}
												loading={submitting || effectiveLoadingSeatInfo}
												disabled={submitting || effectiveUserCount > seats || !authorized}
											>
												{`${T('default.add')} ${seats - effectiveCurrentLicenses} ${T(
													seats - effectiveCurrentLicenses === 1 ? licenseKey : licensesKey
												).toLowerCase()}`}
											</Button>
										) : seats < effectiveCurrentLicenses ? (
											<DangerButton
												size="lg"
												loading={submitting || effectiveLoadingSeatInfo}
												disabled={submitting || !authorized}
												onClick={changeSeats}
											>{`${T('default.remove')} ${effectiveCurrentLicenses - seats} ${T(
												effectiveCurrentLicenses - seats === 1 ? licenseKey : licensesKey
											).toLowerCase()}`}</DangerButton>
										) : (
											<Button size="lg" disabled>
												{T(
													isSupportSeats
														? 'admin.billing.confirmSupportSeats'
														: 'admin.billing.confirmSeats'
												)}
											</Button>
										)}
									</Tooltip>

									<Button onClick={handleCancel} type="link" color="grey">
										{T('default.abort')}
									</Button>
								</div>
								<Text
									color="grey-10"
									align="left"
									size="sm"
									className={classes.elem('confirm-card').elem('invoiceAddress').b()}
								>
									{T('address.invoice')}
								</Text>
								{billingAddress.invoiceDeliveryType === 'mail' ? (
									<>
										{billingAddress.address ? (
											<Text align="left" size="sm">
												{billingAddress.address}
											</Text>
										) : null}
										{billingAddress.zipcode || billingAddress.city ? (
											<Text align="left" size="sm">
												{`${billingAddress.zipcode ? billingAddress.zipcode + ' ' : ''}${
													billingAddress.city || ''
												}`}
											</Text>
										) : null}
										{billingAddress.country ? (
											<Text align="left" size="sm">
												{billingAddress.country}
											</Text>
										) : null}
									</>
								) : (
									<Text align="left" size="sm">
										{billingAddress.email}
									</Text>
								)}
							</Block>
						</Column>
					</Row>
				</CardContent>
			</Card>
		</DrawerWrap>
	);
};

BillingAddSeatDrawer.propTypes = {
	visible: PropTypes.bool,
	intervalPeriod: PropTypes.number,
	nextPage: PropTypes.func,
	seatType: PropTypes.oneOf(['seats', 'supportSeats']),
	loading: PropTypes.bool
};

export const detached = BillingAddSeatDrawer;
export default connect(mapStateToProps, mapDispatchToProps)(BillingAddSeatDrawer);
